import InnerNavbar from '../../components/feature/InnerNavbar';
import InnerFooter from '../../components/feature/InnerFooter';
import AboutHero from './components/AboutHero';
import AboutStory from './components/AboutStory';
import AboutValues from './components/AboutValues';
import AboutFAQ from './components/AboutFAQ';
import TrustBar from '../home/components/TrustBar';
import ServiceArea from '../home/components/ServiceArea';
import Testimonials from '../home/components/Testimonials';
import QuoteForm from '../home/components/QuoteForm';
import { Link } from 'react-router-dom';

const capabilities = [
  {
    icon: 'ri-home-move-line',
    title: 'Residential Moving',
    desc: 'We\'ve moved thousands of Southern California families — from studio apartments to sprawling estates. Every household move gets the same care and attention.',
    link: '/services',
    color: 'from-orange-500 to-red-500',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Commercial & Office Moving',
    desc: 'Our commercial team handles office relocations, retail transitions, and warehouse moves with minimal disruption to your operations — including after-hours scheduling.',
    link: '/services',
    color: 'from-emerald-500 to-teal-500',
  },
  {
    icon: 'ri-route-line',
    title: 'Long-Distance Moving',
    desc: 'Moving out of California? We\'ve handled hundreds of interstate moves with the same reliability and care our local clients expect. We cover all 50 states.',
    link: '/services',
    color: 'from-sky-500 to-cyan-500',
  },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <InnerNavbar activePage="about" />

      {/* 2 — Hero */}
      <AboutHero />

      {/* 3 — TrustBar */}
      <TrustBar />

      {/* 4 — Services (Capabilities) */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block bg-orange-100 text-orange-600 text-sm font-bold px-4 py-2 rounded-full mb-4">
              What We Can Do For You
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              Residential, Commercial &amp; Long-Distance Capability
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              One trusted company for every type of move. No need to shop around.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {capabilities.map((cap) => (
              <div key={cap.title} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:-translate-y-1 transition-all duration-300 group">
                <div className={`h-1.5 bg-gradient-to-r ${cap.color}`}></div>
                <div className="p-7">
                  <div className={`w-14 h-14 flex items-center justify-center bg-gradient-to-br ${cap.color} rounded-2xl mb-5 group-hover:scale-110 transition-transform`}>
                    <i className={`${cap.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-black text-slate-900 mb-3">{cap.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">{cap.desc}</p>
                  <Link to={cap.link}
                    className={`inline-flex items-center gap-2 text-sm font-bold bg-gradient-to-r ${cap.color} bg-clip-text text-transparent hover:opacity-80 transition-opacity`}
                    style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Learn More <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-xl font-bold hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap">
              <i className="ri-grid-line text-lg"></i>See All Moving Services
            </Link>
          </div>
        </div>
      </section>

      {/* 5 — WhyChooseUs (Mission Section) */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/098a4d16a4042d35765bc532ed99d04e.jpg"
                alt="City to City Moving Company delivering furniture to a new Southern California home"
                className="w-full rounded-3xl object-cover object-top"
                style={{ height: '420px' }}
              />
            </div>
            <div>
              <span className="inline-block bg-orange-500/20 text-orange-300 text-sm font-bold px-4 py-2 rounded-full mb-5 border border-orange-500/30">
                Our Mission
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-5 leading-tight">
                Moving Is More Than Lifting Boxes — It&apos;s a New Chapter
              </h2>
              <p className="text-slate-400 text-base leading-relaxed mb-5">
                We understand that every move represents a major life transition. Whether you&apos;re upgrading to your dream home, starting a new chapter across the country, or scaling your business to a new office, the stakes are high. That&apos;s why we approach every job with genuine care.
              </p>
              <p className="text-slate-400 text-base leading-relaxed mb-8">
                Our mission is simple: eliminate the stress of moving so you can focus on what matters most — the exciting new beginning ahead of you. We&apos;re not just movers. We&apos;re the team that helps your next chapter start on the right foot.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="tel:7606257767"
                  className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-7 py-3.5 rounded-xl font-bold hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap">
                  <i className="ri-phone-fill"></i>Talk to Our Team
                </a>
                <Link to="/services"
                  className="flex items-center gap-2 bg-white/10 border border-white/20 text-white px-7 py-3.5 rounded-xl font-semibold hover:bg-white/20 transition-all whitespace-nowrap">
                  <i className="ri-grid-line"></i>Our Services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6 — About (Story) */}
      <AboutStory />

      {/* 7 — Process (Values) */}
      <AboutValues />

      {/* 8 — ServiceArea */}
      <ServiceArea />

      {/* 9 — Testimonials */}
      <Testimonials />

      {/* 10 — FAQ */}
      <AboutFAQ />

      {/* 11 — QuoteForm */}
      <QuoteForm />

      <InnerFooter />

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-6 left-6 z-50 lg:hidden">
        <a
          href="tel:7606257767"
          className="flex items-center gap-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4 rounded-2xl shadow-2xl hover:shadow-amber-500/50 transition-all duration-300 hover:scale-105"
        >
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
            <i className="ri-phone-fill text-xl"></i>
          </div>
          <div className="text-left">
            <div className="text-xs font-medium opacity-90 whitespace-nowrap">Call Now</div>
            <div className="text-sm font-bold whitespace-nowrap">(760) 625-7767</div>
          </div>
        </a>
      </div>

      {/* Floating Quote Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="#quote-form"
          className="flex items-center justify-center w-16 h-16 bg-slate-900 text-white rounded-full shadow-2xl hover:bg-amber-500 transition-all duration-300 hover:scale-110"
        >
          <i className="ri-file-list-3-line text-2xl"></i>
        </a>
      </div>
    </div>
  );
}
